<template>
  <!-- eslint-disable vue/no-v-html -->
  <div v-if="isLoading">
    <base-skeleton>
      <div
        class="skeleton__item pt-3"
        style="min-height: 40px"
      />
      <div
        class="skeleton__item mt-2"
        style="min-height: 650px"
      />
      <div
        class="skeleton__item pt-3"
        style="min-height: 180px"
      />
    </base-skeleton>
  </div>
  <div v-else>
    <base-breadcrumb :routes="routes" />

    <base-card class="pt-3">
      <div class="row">
        <appointment-invitation-preview class="col-9 border-right appointment-preview" />
        <div
          v-if="pastAppointment"
          class="col-3 pl-4 "
        >
          <base-heading level="2">
            Attendees
          </base-heading>
          <base-text
            v-for="(obj, index) in invitees"
            :key="`appointment_invitee_${index}`"
            size="m"
            class="mb-2"
            medium
          >
            {{ displayCustomer(obj.customer, obj.to_profile) }}
          </base-text>
        </div>
        <div
          v-else
          class="col-3 pl-4"
        >
          <base-heading level="2">
            Guests
          </base-heading>
          <div
            class="guest_groups d-flex flex-column"
            :style="`${!recommendation ? 'height: 461px' : ''}`"
          >
            <template v-if="!recommendation">
              <div>
                <base-text
                  underline
                  uppercase
                  inline
                  block
                  medium
                  size="m"
                  class="guest_groups--title"
                >
                  confirmed
                </base-text>
                <!-- CONFIRMED INVITEES -->
                <div
                  v-if="confirmed.length > 0"
                  class="guest_groups--content"
                >
                  <base-text
                    v-for="(obj, index) in confirmed.slice(0, 2)"
                    :key="`appointment_confirmed_${index}`"
                    semibold
                    size="m"
                    class="mb-2"
                  >
                    {{ displayCustomer(obj.customer, obj.to_profile) }}
                  </base-text>
                  <base-text
                    v-if="confirmed.length > 2"
                    light
                    underline
                    inline
                    @click="seeMore('Confirmed')"
                  >
                    See more ({{ confirmed.length - 2 }})
                  </base-text>
                </div>
                <div
                  v-else
                  class="guest_groups--content"
                >
                  <base-text
                    muted
                    semibold
                  >
                    None
                  </base-text>
                </div>
              </div>
              <div>
                <base-text
                  underline
                  uppercase
                  inline
                  block
                  medium
                  size="m"
                  class="guest_groups--title"
                >
                  declined
                </base-text>
                <!-- DECLINED INVITEES -->
                <div
                  v-if="declined.length > 0"
                  class="guest_groups--content"
                >
                  <base-text
                    v-for="(obj, index) in declined.slice(0, 2)"
                    :key="`appointment_declined_${index}`"
                    semibold
                    size="m"
                    class="mb-2"
                  >
                    {{ displayCustomer(obj.customer, obj.to_profile) }}
                  </base-text>
                  <base-text
                    v-if="declined.length > 2"
                    light
                    underline
                    inline
                    @click="seeMore('Declined')"
                  >
                    See more ({{ declined.length - 2 }})
                  </base-text>
                </div>
                <div
                  v-else
                  class="guest_groups--content"
                >
                  <base-text
                    muted
                    semibold
                  >
                    None
                  </base-text>
                </div>
              </div>
              <div>
                <base-text
                  underline
                  uppercase
                  inline
                  block
                  medium
                  size="m"
                  class="guest_groups--title"
                >
                  pending
                </base-text>
                <!-- PENDING INVITEES -->
                <div
                  v-if="pending.length > 0"
                  class="guest_groups--content"
                >
                  <base-text
                    v-for="(obj, index) in pending.slice(0, 2)"
                    :key="`appointment_pending_${index}`"
                    semibold
                    size="m"
                    class="mb-2"
                  >
                    {{ displayCustomer(obj.customer, obj.to_profile) }}
                  </base-text>
                  <base-text
                    v-if="pending.length > 2"
                    light
                    underline
                    inline
                    @click="seeMore('Pending')"
                  >
                    See more ({{ pending.length - 2 }})
                  </base-text>
                </div>
                <div
                  v-else
                  class="guest_groups--content"
                >
                  <base-text
                    muted
                    semibold
                  >
                    None
                  </base-text>
                </div>
              </div>
            </template>
            <template v-else>
              <div
                v-if="invitees.length > 0"
                class="guest_groups--content"
              >
                <base-text
                  semibold
                  size="m"
                  class="mb-2"
                >
                  {{ displayCustomer(invitees[0].customer, null) }}
                </base-text>
              </div>
              <div
                v-else
                class="guest_groups--content"
              >
                <base-text
                  muted
                  semibold
                >
                  None
                </base-text>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div
        v-if="!pastAppointment && !recommendation"
        class="row mt-3"
      >
        <div class="col-9 d-flex actions">
          <base-tooltip
            v-if="hasJoinLink"
            :text="tooltipText"
          >
            <base-button
              borderless
              nowrap
              :disabled="isLoading"
              class="px-0 mx-0 grey"
              @click="copyVideoLink()"
            >
              <base-icon
                icon="create-link"
                class="mr-2"
              />
              Copy video link
            </base-button>
          </base-tooltip>
          <base-button
            borderless
            nowrap
            :disabled="isLoading"
            class="px-0 mx-0 grey"
            @click="downloadICS()"
          >
            <base-icon
              icon="schedule"
              class="mr-2"
            />
            <base-text semibold>
              Download to calendar
            </base-text>
          </base-button>
          <base-button
            borderless
            nowrap
            :disabled="isLoading"
            class="px-0 mx-0 grey"
            @click="resendInvitations()"
          >
            <base-icon
              icon="send-test"
              class="mr-2"
            />
            <base-text semibold>
              Resend invite
            </base-text>
          </base-button>
          <base-button
            borderless
            nowrap
            :disabled="isLoading"
            class="px-0 mx-0 grey"
            @click="preDeleteAppointment()"
          >
            <base-icon
              icon="appointment-cancel"
              class="mr-2"
            />
            <base-text semibold>
              Cancel appointment
            </base-text>
          </base-button>
        </div>
      </div>
      <div
        v-if="!pastAppointment && !recommendation"
        class="w-100 text-right mt-4"
      >
        <base-button
          v-if="hasJoinLink"
          class="px-3 py-2 join-button"
          @click="goToAppointment()"
        >
          Join Now
        </base-button>
        <base-button
          inverse
          class="px-3 py-2"
          @click="goToEdit"
        >
          Edit Appointment
        </base-button>
      </div>
    </base-card>
    <base-card class="mt-3">
      <base-heading>Products</base-heading>
      <div
        v-if="pastAppointment"
        class="cursor-pointer my-2 d-flex justify-content-center"
      >
        <template v-if="content.product_images.length > 0">
          <base-text
            v-if="!creatingEdit"
            underline
            @click="createEdit"
          >
            Create an edit with these products
          </base-text>
          <base-text v-else>
            Creating...
          </base-text>
        </template>
      </div>
      <product-row-component
        class="products-widget"
        :label="!pastAppointment ? 'Add products for your upcoming appointment!' :
          content.product_images.length === 0 ? 'No products were added.' : ''"
        :disabled="pastAppointment"
        :content="content"
        @contentUpdated="onProductsUpdated"
      />
    </base-card>
    <base-modal
      id="guest-list"
      ref="guest-list"
      size="modal-md"
      :show="modalState !== 'Closed'"
      :show-close-button="true"
      @closed="modalClosed"
    >
      <div>
        <base-text
          medium
          class="mb-3"
        >
          Guests - {{ modalState }}
        </base-text>
        <base-text>
          {{ item.subject | striphtml }}
        </base-text>
        <base-text
          light
          muted
          size="s"
        >
          {{ evaluateDate(item.start_date, item.end_date) }}
        </base-text>
        <base-text
          light
          muted
          size="s"
        >
          {{ item.start_date | nicedate('custom', 'h:mm A') }} to
          {{ item.end_date | nicedate('custom', 'h:mm A') }}
        </base-text>
      </div>
    </base-modal>
  </div>
</template>
<script>
// eslint-disable-next-line import/no-extraneous-dependencies
import moment from 'moment';
import ProductRowComponent from '@/components/ProductRowComponent';
import { APPOINTMENT_TYPES } from '@/_utilities/Enums';
import { EventBus } from '@/event-bus';
// import { appointmentsService } from '../../../_services';
import emailEngine from '@/components/mixins/emailEngine';

export default {
  name: 'AppointmentSummaryContainer',
  components: { ProductRowComponent },
  mixins: [emailEngine],
  data() {
    return {
      modalState: 'Closed',
      loading: true,
      products: [],
      showDeleteDialog: false,
      tooltipText: 'Copy to clipboard',
    };
  },
  computed: {
    isLoading() {
      return this.loading;
    },
    show_stream_key() {
      return this.item.appointment_type === APPOINTMENT_TYPES.LIVESTREAM;
    },
    routes() {
      return [
        {
          name: 'Appointments',
          link: '/appointments',
        },
        {
          name: this.item.subject,
        },
      ];
    },
    appointmentId() {
      return this.$route.params.id;
    },
    item() {
      return this.$store.state.appointments.item;
    },
    invitees() {
      if (Object.keys(this.item).length === 0 || this.item.invitations === undefined) {
        return [];
      }
      return this.item.invitations.filter((item) => item.customer !== null);
    },
    confirmed() {
      return this.invitees.filter((item) => item.status === 'CO');
    },
    pending() {
      return this.invitees.filter((item) => item.status === 'PE');
    },
    declined() {
      return this.invitees.filter((item) => item.status === 'DE');
    },
    content() {
      if (Object.keys(this.item).length === 0 && this.item.constructor === Object) {
        return { product_images: [] };
      }
      return { product_images: this.item.products };
    },
    pastAppointment() {
      if (Object.keys(this.item).length === 0 && this.item.constructor === Object) {
        return false;
      }
      return this.$moment(this.item.end_date) < this.$moment();
    },
    creatingEdit() {
      return this.$store.state.entries.isLoading;
    },
    duration() {
      let duration = '';
      if (this.item.duration?.hours) {
        duration += `${this.item.duration.hours}h `;
      }
      duration += this.item.duration ? `${this.item.duration.minutes}m duration` : '--';
      return duration;
    },
    inPerson() {
      return this.item.appointment_type === APPOINTMENT_TYPES.IN_PERSON;
    },
    recommendation() {
      return this.item.appointment_type === APPOINTMENT_TYPES.RECOMMENDATION;
    },
    hasJoinLink() {
      return !this.inPerson && !this.recommendation;
    },
  },
  mounted() {
    this.fetchItem();
  },
  methods: {
    seeMore(type) {
      this.$router.push(`/appointments/${this.appointmentId}/view/guests/${type}`);
    },
    modalClosed() {
      this.modalState = 'Closed';
    },
    roomAvailable(item) {
      const before = moment(item.start_date).subtract(115, 'minutes');
      const after = moment(item.end_date);
      return moment().isBetween(before, after);
    },

    goToAppointment() {
      window.open(this.item.join_link, '_blank');
    },
    createEdit() {
      this.$store.dispatch('entries/newItem', this.$route.params.id);
    },
    copyVideoLink() {
      this.tooltipText = 'Copied!';
      window.Clipboard.copy(this.item.invite_link);
      setTimeout(() => {
        this.tooltipText = 'Copy to clipboard';
      }, 30000);
    },
    preDeleteAppointment() {
      EventBus.$emit('confirm', {
        title: 'Cancel Appointment',
        message:
          `Are you sure you want to cancel and delete this appointment? We will remove it from your Appointments page${this.recommendation ? '.' : ' and send a cancellation email to your invitees.'}`,
        positiveText: 'Confirm',
        negativeText: 'Keep appointment',
        callback: (confirm) => {
          if (confirm) {
            this.onDeleteAppointment();
          }
        },
      });
    },
    goToEdit() {
      this.$router.push(`/appointments/${this.appointmentId}/edit`);
    },
    fetchItem() {
      this.$store.dispatch('appointments/getItem', this.appointmentId).then((data) => {
        this.loading = false;
      });
    },
    /* fetchInvitations() {
      this.$store.dispatch('invitations/fetchAppointmentInvitations', this.appointmentId);
    }, */
    onProductsUpdated(newProducts) {
      this.products = newProducts;
      const id = this.appointmentId;
      const payload = { products: newProducts.product_images.map((item) => item.pk) };
      this.$store.dispatch('appointments/updateAppointment', { id, payload }).then((data) => {
        this.loading = true;
        this.fetchItem();
      });
    },
    onCancelClicked() {
      this.$router.push('/appointments');
    },
    displayCustomer(customer, toProfile) {
      if (!customer) {
        // it means there _is_ a profile
        const name = [];
        ['first_name', 'last_name'].forEach((n) => {
          if (toProfile[n]) name.push(toProfile[n]);
        });
        return name.join(' ').trim() || toProfile.email;
      }
      if (customer.first_name) {
        if (customer.last_name) {
          return `${customer.first_name} ${customer.last_name}`;
        }
        return customer.first_name;
      }
      return customer.email;
    },
    evaluateDate(startDate, endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (start.toDateString() === end.toDateString()) {
        return this.$options.filters.nicedate(start, 'small');
      }
      return `${this.$options.filters.nicedate(start, 'small')} to: ${this.$options.filters.nicedate(end, 'small')}`;
    },
    dispatchResendInvitations({ id }) {
      return this.$store.dispatch('appointments/resendInvitations', {
        id,
        authCode: this.authCode,
      });
    },
    resendInvitations() {
      if (this.recommendation) {
        console.warn('Resend invitations is not supported for Recommendation');
        return;
      }
      const { id } = this.$route.params;
      if (this.emailEngine.name === 'Email') {
        this.dispatchResendInvitations({ id });
      } else {
        this.emailEngine.action({ id }, this.dispatchResendInvitations);
      }
    },
    onDeleteAppointment() {
      const { id } = this.$route.params;
      this.$store
        .dispatch('appointments/deleteItem', id)
        .then(() => {
          this.$router.push('/appointments');
        })
        .catch(() => {
          EventBus.$emit(
            'toast-error',
            "There's been an error while cancelling this appointment, please try again in a few seconds.",
          );
        })
        .finally(() => {});
    },
    downloadICS() {
      this.$store.dispatch('appointments/downloadICS', this.item.pk).then((response) => {
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        const fname = `${this.item.subject.replaceAll(' ', '-')}.ics`;
        fileLink.setAttribute('download', fname);
        document.body.appendChild(fileLink);
        fileLink.click();
        document.body.removeChild(fileLink);
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.dates {
  color: $gray;
}

::v-deep .border-right {
  border-right: 1px black solid !important;
  &--past {
    border-right: 1px $gray-light-2 solid !important;
  }
}

.products-widget {
  ::v-deep .card-body {
    border: 1px solid #d4d4d4;
    border-radius: 5px;
  }
}
.intro {
  border-radius: 5px;
  background-color: $gray-light;
  min-height: 357px;
  padding: 39px 20px;
}
::v-deep .quill-hashtag {
  background-color: #d4d4d4 !important;
}
::v-deep .base-button {
  font-size: 14px;
  &.clementine {
    color: $clementine;
    font-family: $font-family-sans-serif-medium;
  }
  &.grey {
    color: #646464;
    font-family: $font-family-sans-serif-medium;
  }
}
.guest_groups {
  display: flex;
  ::v-deep .base-text {
    color: $black;
    &--muted {
      color: $gray-light-2;
    }
  }
  &--title {
    margin-bottom: 17px;
  }
  &--content {
    margin-bottom: 32px;

  }
}
.join-button {
  background: #e9ad7d;
  color: white;
  border: solid 0;
}
.actions {
  gap: 28px;
  ::v-deep .base-icon {
    width: 18px;
  }
}
.appointment-preview {
  border-radius: 0;
  ::v-deep .card-body {
    padding: 0;
  }
}
</style>
